import React, { useEffect, useState, Children } from 'react';
import { motion } from 'framer-motion';
import { ArrowRightIcon, CheckIcon, CpuIcon, WrenchIcon, ShieldIcon, RefreshCwIcon, EyeIcon, BrainIcon, FileTextIcon, AlertTriangleIcon, UsersIcon, BuildingIcon, ZapIcon, LayersIcon, SearchIcon, PaletteIcon } from 'lucide-react';
import { CTABanner } from '../../components/CTABanner';
const fadeUp = {
  hidden: {
    opacity: 0,
    y: 20
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.5
    }
  }
};
const stagger = {
  hidden: {},
  visible: {
    transition: {
      staggerChildren: 0.1
    }
  }
};
function SectionDivider() {
  return <div className="absolute top-0 left-1/2 -translate-x-1/2 w-1/2 h-px bg-gradient-to-r from-transparent via-violet-electric/20 to-transparent" data-id="element-1383" data-mp="2x5uwf45dl5b3" />;
}
export function CustomPage() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  return <div className="min-h-screen w-full bg-navy" data-id="element-1384" data-mp="2s5gqbrnc45fp">
      {/* ── SECTION 1: HERO ── */}
      <section className="relative w-full min-h-screen flex items-center justify-center pt-32 pb-24 px-6 overflow-hidden bg-navy" data-id="element-1385" data-mp="2meyl08ksf346">
        {/* Background glow */}
        <div className="absolute inset-0 pointer-events-none" style={{
        background: 'radial-gradient(ellipse 80% 60% at 50% 0%, rgba(108,92,231,0.22) 0%, rgba(200,80,192,0.07) 50%, transparent 80%)'
      }} data-id="element-1386" data-mp="2hfvs1mi30nck" />
        
        <div className="absolute bottom-0 left-0 right-0 h-32 pointer-events-none" style={{
        background: 'linear-gradient(to top, #0D0B2E, transparent)'
      }} data-id="element-1387" data-mp="23oqnc5zux4vd" />
        

        <div className="relative z-10 max-w-3xl mx-auto text-center" data-id="element-1388" data-mp="1ynjfy81mm7p8">
          <motion.span className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-violet-electric/30 bg-violet-electric/10 text-violet-electric text-xs font-semibold tracking-[0.15em] uppercase mb-6" initial={{
          opacity: 0,
          y: 12
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.4,
          delay: 0.1
        }} data-id="element-1389" data-mp="2chu7sy8x6hzi">
            
            Thruline Core
          </motion.span>

          <motion.h1 className="text-5xl sm:text-6xl md:text-7xl font-bold text-white leading-[1.05] tracking-tight mb-6" initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.55,
          delay: 0.2
        }} data-id="element-1390" data-mp="3k27o053fah1u">
            
            A custom-developed ERP.
            <br data-id="element-1391" data-mp="2w7epxmxhmos4" />
            <span className="text-transparent bg-clip-text" style={{
            backgroundImage: 'linear-gradient(90deg, #a78bfa, #C850C0)'
          }} data-id="element-1392" data-mp="313vak75hal0g">
              
              No developers needed.
            </span>
          </motion.h1>

          <motion.p className="text-lg sm:text-xl text-white/55 max-w-2xl mx-auto mb-10 leading-relaxed" initial={{
          opacity: 0,
          y: 16
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.5,
          delay: 0.3
        }} data-id="element-1393" data-mp="3b3vyk6ealpaz">
            
            Thruline helps you plan and build a system configured to your
            actual operations. You own it, understand every workflow and rule,
            and evolve it yourself as your business grows — with AI assistance
            and expert advisory when you need it.
          </motion.p>

          <motion.div className="flex flex-col sm:flex-row items-center justify-center gap-4" initial={{
          opacity: 0,
          y: 16
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.5,
          delay: 0.4
        }} data-id="element-1394" data-mp="1w4yjde70ngvv">
            
            <a href="https://calendly.com/alex-thruline/learn-about-thruline" className="inline-flex items-center gap-2 px-8 py-3.5 rounded-full bg-white text-navy font-semibold text-sm hover:bg-white/90 transition-all hover:shadow-lg hover:shadow-white/10" data-id="element-1395" data-mp="1i5p9ew9elj0c">
              
              See how it works
              <ArrowRightIcon className="w-4 h-4" data-id="element-1396" data-mp="37ian7p2rzzi1" />
            </a>
            <a href="https://calendly.com/alex-thruline/learn-about-thruline" className="inline-flex items-center px-8 py-3.5 rounded-full border border-white/25 text-white font-medium text-sm hover:border-white/50 transition-all" data-id="element-1397" data-mp="258afohuviggq">
              
              Talk to our team
            </a>
          </motion.div>
        </div>
      </section>

      {/* ── SECTION 2: THE PROBLEM ── */}
      <section className="relative w-full bg-navy-light py-24 px-6" data-id="element-1398" data-mp="1lhx9gx1pvv38">
        <SectionDivider data-id="element-1399" data-mp="1q9ge6q6rvl61" />
        <div className="max-w-3xl mx-auto" data-id="element-1400" data-mp="1qaqezn50f7gd">
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true,
          margin: '-60px'
        }} variants={stagger} data-id="element-1401" data-mp="1qbt5htyf77vr">
            
            <motion.span variants={fadeUp} className="text-violet-electric text-xs font-semibold tracking-[0.2em] uppercase block mb-4" data-id="element-1402" data-mp="1hi6bo9drwep7">
              
              The Problem
            </motion.span>
            <motion.h2 variants={fadeUp} className="text-3xl sm:text-4xl font-bold text-white tracking-tight mb-12" data-id="element-1403" data-mp="17hw0eubathco">
              
              You know your business.
              <br data-id="element-1404" data-mp="jwll352ym397" />
              Your ERP doesn't.
            </motion.h2>

            <div className="space-y-8" data-id="element-1405" data-mp="xvla4dudwk5w">
              <motion.div variants={fadeUp} className="flex gap-5" data-id="element-1406" data-mp="xwmoar6p5mru">
                <div className="flex-shrink-0 w-8 h-8 rounded-full bg-red-500/10 border border-red-500/20 flex items-center justify-center mt-0.5" data-id="element-1407" data-mp="swrxenmx8x6s">
                  <span className="text-red-400 text-xs font-bold" data-id="element-1408" data-mp="16uq3z6v03pbq">1</span>
                </div>
                <div data-id="element-1409" data-mp="12u68s8tzmvs8">
                  <p className="text-white/70 text-base leading-relaxed" data-id="element-1410" data-mp="12sv7qgrxg24t">
                    <span className="text-white font-medium" data-id="element-1411" data-mp="1glkwuu2r5n65">
                      Off-the-shelf ERP forces you to work the way the software
                      was designed
                    </span>
                    , not the way your operation actually runs. You end up with
                    workarounds, shadow spreadsheets, and processes that exist
                    only because the system can't handle how you actually do
                    things.
                  </p>
                </div>
              </motion.div>

              <motion.div variants={fadeUp} className="flex gap-5" data-id="element-1412" data-mp="22mjsu5fwehq2">
                <div className="flex-shrink-0 w-8 h-8 rounded-full bg-amber-500/10 border border-amber-500/20 flex items-center justify-center mt-0.5" data-id="element-1413" data-mp="22l8pg6qd7bzh">
                  <span className="text-amber-400 text-xs font-bold" data-id="element-1414" data-mp="2ccwc7ubz84um">2</span>
                </div>
                <div data-id="element-1415" data-mp="2gdf1bgmhiwt4">
                  <p className="text-white/70 text-base leading-relaxed" data-id="element-1416" data-mp="2gcn7obcg795h">
                    <span className="text-white font-medium" data-id="element-1417" data-mp="2lcjesjgfzgp9">
                      Hiring a team to build something custom solves the fit
                      problem
                    </span>{' '}
                    — but requires developers, implementation consultants, and
                    ongoing technical staff that most manufacturers can't
                    justify. The cost of having your own system has always been
                    the cost of having your own team.
                  </p>
                </div>
              </motion.div>

              <motion.div variants={fadeUp} className="flex gap-5" data-id="element-1418" data-mp="jlsnhyw1ynp1">
                <div className="flex-shrink-0 w-8 h-8 rounded-full bg-blue-500/10 border border-blue-500/20 flex items-center justify-center mt-0.5" data-id="element-1419" data-mp="epkdt10mjmp9">
                  <span className="text-blue-400 text-xs font-bold" data-id="element-1420" data-mp="17dg33knv6cc0">3</span>
                </div>
                <div data-id="element-1421" data-mp="12edaizp2lykx">
                  <p className="text-white/70 text-base leading-relaxed" data-id="element-1422" data-mp="12dtpvb81cw37">
                    <span className="text-white font-medium" data-id="element-1423" data-mp="xfa358lmbg9s">
                      Handing it to a managed service provider gets you a custom
                      system
                    </span>{' '}
                    — but you trade one dependency for another. Every change
                    goes through their queue. Every modification is on their
                    timeline. You use the system, but you never truly own it.
                  </p>
                </div>
              </motion.div>
            </div>

            <motion.div variants={fadeUp} className="mt-12 pt-8 border-t border-white/[0.06]" data-id="element-1424" data-mp="bosc7yqafcbv">
              
              <p className="text-xl font-semibold text-white" data-id="element-1425" data-mp="3tux6eru3ubd0">
                Thruline Core gives you the system without the team — and the
                control without the dependency.
              </p>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* ── SECTION 3: WHAT IS THRULINE CORE ── */}
      <section className="relative w-full bg-navy py-24 px-6" data-id="element-1426" data-mp="udytdhbfnvvk">
        <SectionDivider data-id="element-1427" data-mp="gmal5p4b1rjv" />
        <div className="max-w-5xl mx-auto" data-id="element-1428" data-mp="gmu0qz6hg3uq">
          <motion.div className="text-center mb-16" initial="hidden" whileInView="visible" viewport={{
          once: true,
          margin: '-60px'
        }} variants={stagger} data-id="element-1429" data-mp="kop56cjpmxcq">
            
            <motion.span variants={fadeUp} className="text-violet-electric text-xs font-semibold tracking-[0.2em] uppercase block mb-4" data-id="element-1430" data-mp="1d4zsq6ggttfn">
              
              What Is Thruline Core
            </motion.span>
            <motion.h2 variants={fadeUp} className="text-3xl sm:text-4xl font-bold text-white tracking-tight max-w-2xl mx-auto" data-id="element-1431" data-mp="2sx3q06yu2d2s">
              
              Your own operating system. Built together. Owned by you.
            </motion.h2>
          </motion.div>

          <motion.div className="grid grid-cols-1 md:grid-cols-2 gap-5" initial="hidden" whileInView="visible" viewport={{
          once: true,
          margin: '-40px'
        }} variants={stagger} data-id="element-1432" data-mp="32ogeqppc4n4l">
            
            {/* A: Custom-Developed */}
            <motion.div variants={fadeUp} className="rounded-2xl border border-white/[0.06] bg-white/[0.03] p-8" data-id="element-1433" data-mp="2ow07fcpei2xv">
              
              <div className="w-10 h-10 rounded-xl bg-violet-electric/15 flex items-center justify-center mb-5" data-id="element-1434" data-mp="2jukobq6sa6i4">
                <LayersIcon className="w-5 h-5 text-violet-electric" data-id="element-1435" data-mp="46kmuzgrzago" />
              </div>
              <h3 className="text-lg font-semibold text-white mb-3" data-id="element-1436" data-mp="3vd5asabja29h">
                Custom-Developed to Your Operation
              </h3>
              <p className="text-white/55 text-sm leading-relaxed" data-id="element-1437" data-mp="1owo33xj7rtqh">
                Core isn't a template you configure — it's a platform where
                your actual workflows, rules, and processes become the system.
                Production scheduling, inventory, purchasing, quality, shipping
                — all built around how your operation actually works, not how a
                vendor thinks it should.
              </p>
            </motion.div>

            {/* B: Built Together */}
            <motion.div variants={fadeUp} className="rounded-2xl border border-white/[0.06] bg-white/[0.03] p-8" data-id="element-1438" data-mp="1173kjfqz2yhq">
              
              <div className="w-10 h-10 rounded-xl bg-violet-electric/15 flex items-center justify-center mb-5" data-id="element-1439" data-mp="wa3bcvyttfme">
                <UsersIcon className="w-5 h-5 text-violet-electric" data-id="element-1440" data-mp="2vkuz8to5s8k1" />
              </div>
              <h3 className="text-lg font-semibold text-white mb-3" data-id="element-1441" data-mp="2qn2w13i2d00j">
                Thruline Is Your Development Team
              </h3>
              <p className="text-white/55 text-sm leading-relaxed" data-id="element-1442" data-mp="3355kgtli8xrv">
                You don't start with a blank screen. Thruline's AI reads your
                existing documents to map your operation, then our team builds
                the first version with you — walking you through every
                workflow, every rule, and why it was configured that way. The
                goal isn't just a working system. It's a working system you
                understand.
              </p>
            </motion.div>

            {/* C: Full Transparency & Ownership */}
            <motion.div variants={fadeUp} className="rounded-2xl border border-white/[0.06] bg-white/[0.03] p-8" data-id="element-1443" data-mp="2p42zyla5rvj2">
              
              <div className="w-10 h-10 rounded-xl bg-violet-electric/15 flex items-center justify-center mb-5" data-id="element-1444" data-mp="4ff7v9zu2knx">
                <EyeIcon className="w-5 h-5 text-violet-electric" data-id="element-1445" data-mp="9dqfrf6kil3i" />
              </div>
              <h3 className="text-lg font-semibold text-white mb-3" data-id="element-1446" data-mp="25h4mnoh3jq4z">
                Full Transparency. Full Ownership.
              </h3>
              <p className="text-white/55 text-sm leading-relaxed" data-id="element-1447" data-mp="20hi8nctg25v4">
                Every workflow, rule, automation, and connection in your system
                is visible. Nothing is hidden in proprietary code or managed
                behind a curtain. You can open any process and see exactly how
                it works, what triggers it, and what documents informed its
                design. You can't evolve what you can't see — so Core shows
                you everything.
              </p>
            </motion.div>

            {/* D: Evolve It Yourself */}
            <motion.div variants={fadeUp} className="rounded-2xl border border-violet-electric/20 bg-violet-electric/[0.04] p-8" data-id="element-1448" data-mp="1qryps75qec09">
              
              <div className="w-10 h-10 rounded-xl bg-violet-electric/20 flex items-center justify-center mb-5" data-id="element-1449" data-mp="131347rrvcd1g">
                <RefreshCwIcon className="w-5 h-5 text-violet-electric" data-id="element-1450" data-mp="yz806fijzljz" />
              </div>
              <h3 className="text-lg font-semibold text-white mb-3" data-id="element-1451" data-mp="ovauggohfojn">
                Evolve It Yourself
              </h3>
              <p className="text-white/55 text-sm leading-relaxed" data-id="element-1452" data-mp="ez7wzo4kse31">
                When your business changes, you change the system — directly.
                Describe what you need in plain language and AI suggests the
                configuration changes. See exactly what each modification
                affects before it goes live. Flag anything for Thruline expert
                review when the stakes are high. No tickets. No waiting. No
                change orders. You hold the wrench.
              </p>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* ── SECTION 4: WHO IT'S FOR ── */}
      <section className="relative w-full bg-navy-light py-24 px-6" data-id="element-1453" data-mp="3ievicctfhkx9">
        <SectionDivider data-id="element-1454" data-mp="3nfj3wiyw19k3" />
        <div className="max-w-3xl mx-auto" data-id="element-1455" data-mp="3ng2k9v8w3pw0">
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true,
          margin: '-60px'
        }} variants={stagger} data-id="element-1456" data-mp="3ncn6qwuv6owp">
            
            <motion.span variants={fadeUp} className="text-violet-electric text-xs font-semibold tracking-[0.2em] uppercase block mb-4" data-id="element-1457" data-mp="2ogsswhcsr7s">
              
              Who It's For
            </motion.span>
            <motion.h2 variants={fadeUp} className="text-3xl sm:text-4xl font-bold text-white tracking-tight mb-4" data-id="element-1458" data-mp="3oyt5v64y6bz8">
              
              Built for manufacturers who want to own their system — not just
              use it.
            </motion.h2>
            <motion.p variants={fadeUp} className="text-white/50 text-base mb-10" data-id="element-1459" data-mp="ggwzq8ddl9dz">
              
              Thruline Core is for you if:
            </motion.p>

            <motion.div variants={stagger} className="space-y-4" data-id="element-1460" data-mp="bkg3srzxpqqc">
              {["You're a manufacturer with 50–500 employees running operations that have outgrown spreadsheets and basic software", "You have no development team, or a very small one that's already stretched thin", "You've been burned by off-the-shelf ERP that didn't fit, or custom ERP you couldn't maintain", 'You want to understand and modify your system without calling a vendor every time something changes', 'You want the kind of system that only companies with full dev teams and internal consultants can typically build — without hiring them'].map((item, i) => <motion.div key={i} variants={fadeUp} className="flex items-start gap-4" data-id="element-1461" data-mp="1vesva7pklmzr" data-mpi={i}>
                
                  <div className="flex-shrink-0 w-6 h-6 rounded-full bg-violet-electric/15 border border-violet-electric/25 flex items-center justify-center mt-0.5" data-id="element-1462" data-mp="1zfl5p4a6hxxm">
                    <CheckIcon className="w-3.5 h-3.5 text-violet-electric" data-id="element-1463" data-mp="24d4moby9got9" />
                  </div>
                  <p className="text-white/70 text-base leading-relaxed" data-id="element-1464" data-mp="29bxylzxwg7rb">
                    {item}
                  </p>
                </motion.div>)}
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* ── SECTION 5: HOW IT WORKS ── */}
      <section className="relative w-full bg-navy py-24 px-6" data-id="element-1465" data-mp="703fgzdfvrmf">
        <SectionDivider data-id="element-1466" data-mp="2yhtmoixg303" />
        <div className="max-w-4xl mx-auto" data-id="element-1467" data-mp="2zb4o1ox5eo1">
          <motion.div className="text-center mb-16" initial="hidden" whileInView="visible" viewport={{
          once: true,
          margin: '-60px'
        }} variants={stagger} data-id="element-1468" data-mp="gxrbrtqp176p">
            
            <motion.span variants={fadeUp} className="text-violet-electric text-xs font-semibold tracking-[0.2em] uppercase block mb-4" data-id="element-1469" data-mp="c10vp6ew2bla">
              
              How It Works
            </motion.span>
            <motion.h2 variants={fadeUp} className="text-3xl sm:text-4xl font-bold text-white tracking-tight" data-id="element-1470" data-mp="zo90ln8enims">
              
              Thruline helps you plan and build it. You own it from there.
            </motion.h2>
          </motion.div>

          <motion.div className="space-y-5" initial="hidden" whileInView="visible" viewport={{
          once: true,
          margin: '-40px'
        }} variants={stagger} data-id="element-1471" data-mp="19n7zwf7a61x4">
            
            {/* Phase 1 */}
            <motion.div variants={fadeUp} className="rounded-2xl border border-white/[0.06] bg-white/[0.02] overflow-hidden" data-id="element-1472" data-mp="1o9na6vobabyv">
              
              <div className="flex items-start gap-6 p-8" data-id="element-1473" data-mp="1eb7q3cnpc2yv">
                <div className="flex-shrink-0" data-id="element-1474" data-mp="1eapm1tusskge">
                  <div className="w-12 h-12 rounded-xl bg-violet-electric/15 border border-violet-electric/20 flex items-center justify-center" data-id="element-1475" data-mp="1j9izjma34bgk">
                    <BrainIcon className="w-6 h-6 text-violet-electric" data-id="element-1476" data-mp="14juymm5tfetd" />
                  </div>
                </div>
                <div className="flex-1" data-id="element-1477" data-mp="19dz38yrlzara">
                  <div className="flex items-center gap-3 mb-3" data-id="element-1478" data-mp="vl98a4u3qvi1">
                    <span className="text-violet-electric text-xs font-bold tracking-widest uppercase" data-id="element-1479" data-mp="vin3gcyliu3i">
                      Phase 1
                    </span>
                    <span className="text-white/20 text-xs" data-id="element-1480" data-mp="lmts2wwm56nb">—</span>
                    <span className="text-white font-semibold" data-id="element-1481" data-mp="qlmu9t6g9b86">Discovery</span>
                    <span className="ml-auto px-2.5 py-0.5 rounded-full bg-emerald-500/10 border border-emerald-500/20 text-emerald-400 text-[10px] font-bold tracking-wide uppercase" data-id="element-1482" data-mp="1634nc7mv6kqk">
                      Free
                    </span>
                  </div>
                  <p className="text-white/55 text-sm leading-relaxed mb-4" data-id="element-1483" data-mp="x0vedm78uzfa">
                    Thruline's AI reads your existing documents — SOPs,
                    spreadsheets, emails, PDFs — and maps your actual
                    operational processes. Our team reviews the results and
                    builds a configuration blueprint: what your system needs,
                    how it should work, and why.
                  </p>
                  <p className="text-white/40 text-sm" data-id="element-1484" data-mp="9ddu0bqb73mc">
                    <span className="text-white/60 font-medium" data-id="element-1485" data-mp="9eymlvt2cchk">
                      What you get:
                    </span>{' '}
                    A clear picture of your operation translated into system
                    architecture. No guesswork. No months of consultant
                    interviews.
                  </p>
                </div>
              </div>
            </motion.div>

            {/* Phase 2 */}
            <motion.div variants={fadeUp} className="rounded-2xl border border-white/[0.06] bg-white/[0.02] overflow-hidden" data-id="element-1486" data-mp="3vel5yabu0ehq">
              
              <div className="flex items-start gap-6 p-8" data-id="element-1487" data-mp="478p5wev232c">
                <div className="flex-shrink-0" data-id="element-1488" data-mp="32pxdnuso0ouo">
                  <div className="w-12 h-12 rounded-xl bg-violet-electric/15 border border-violet-electric/20 flex items-center justify-center" data-id="element-1489" data-mp="32r8nab8skqtp">
                    <WrenchIcon className="w-6 h-6 text-violet-electric" data-id="element-1490" data-mp="3co3jgnhlef4t" />
                  </div>
                </div>
                <div className="flex-1" data-id="element-1491" data-mp="3hfbshif0fyhg">
                  <div className="flex items-center gap-3 mb-3" data-id="element-1492" data-mp="3hgd69uneuz2v">
                    <span className="text-violet-electric text-xs font-bold tracking-widest uppercase" data-id="element-1493" data-mp="2k07llfgaeh6g">
                      Phase 2
                    </span>
                    <span className="text-white/20 text-xs" data-id="element-1494" data-mp="2ow66uu6bjj2n">—</span>
                    <span className="text-white font-semibold" data-id="element-1495" data-mp="l0xuqkikrqjk">
                      First Build
                    </span>
                    <span className="ml-auto px-2.5 py-0.5 rounded-full bg-violet-electric/10 border border-violet-electric/20 text-violet-electric text-[10px] font-bold tracking-wide uppercase" data-id="element-1496" data-mp="q2di4j886954">
                      Fixed Quote
                    </span>
                  </div>
                  <p className="text-white/55 text-sm leading-relaxed mb-4" data-id="element-1497" data-mp="2csz60c2nbvu">
                    Thruline builds the initial system with you using the
                    discovery blueprint. Every configuration is transparent,
                    documented, and explained. We walk you through what was
                    built and why, tying each decision back to the documents
                    and processes that informed it. You're not just getting a
                    system — you're learning how it works.
                  </p>
                  <p className="text-white/40 text-sm" data-id="element-1498" data-mp="3c7org8fzuu8w">
                    <span className="text-white/60 font-medium" data-id="element-1499" data-mp="3c75llq3mbwb8">
                      What you get:
                    </span>{' '}
                    A working system configured to your operation, on a
                    platform you fully understand and can modify. Not a black
                    box.
                  </p>
                </div>
              </div>
            </motion.div>

            {/* Phase 3 */}
            <motion.div variants={fadeUp} className="rounded-2xl border border-violet-electric/15 bg-violet-electric/[0.03] overflow-hidden" data-id="element-1500" data-mp="2taxfkk2s7hev">
              
              <div className="flex items-start gap-6 p-8" data-id="element-1501" data-mp="2jk2s5cdsubru">
                <div className="flex-shrink-0" data-id="element-1502" data-mp="2fhy0gj6wvp7u">
                  <div className="w-12 h-12 rounded-xl bg-violet-electric/20 border border-violet-electric/30 flex items-center justify-center" data-id="element-1503" data-mp="2fj94mk49qyzh">
                    <RefreshCwIcon className="w-6 h-6 text-violet-electric" data-id="element-1504" data-mp="25jrzc3sxin41" />
                  </div>
                </div>
                <div className="flex-1" data-id="element-1505" data-mp="1vvmmk4l3hpr2">
                  <div className="flex items-center gap-3 mb-3" data-id="element-1506" data-mp="1vul3bctxdwy9">
                    <span className="text-violet-electric text-xs font-bold tracking-widest uppercase" data-id="element-1507" data-mp="20w0k2sp2lhau">
                      Phase 3
                    </span>
                    <span className="text-white/20 text-xs" data-id="element-1508" data-mp="1qwtbeita6au2">—</span>
                    <span className="text-white font-semibold" data-id="element-1509" data-mp="1qx1mc55uf62m">
                      Operate & Evolve
                    </span>
                    <span className="ml-auto px-2.5 py-0.5 rounded-full bg-blue-500/10 border border-blue-500/20 text-blue-400 text-[10px] font-bold tracking-wide uppercase" data-id="element-1510" data-mp="2jlgxgglut48o">
                      Platform License
                    </span>
                  </div>
                  <p className="text-white/55 text-sm leading-relaxed mb-4" data-id="element-1511" data-mp="29tuh65unsez2">
                    You run your business on Core. When something changes —
                    new product line, new process, new requirement — you
                    describe the change in plain language and the AI suggests
                    configuration modifications. You see the impact before
                    anything goes live. Flag changes for Thruline expert
                    review when you want a second opinion. You have day-to-day
                    control. Thruline advisory support is there when you need
                    it.
                  </p>
                  <p className="text-white/40 text-sm" data-id="element-1512" data-mp="7xlnw7aqozwx">
                    <span className="text-white/60 font-medium" data-id="element-1513" data-mp="807tw2hzf7d1">
                      What you get:
                    </span>{' '}
                    A system that evolves with your business — on your
                    timeline, not a vendor's. Ongoing control without ongoing
                    dependency.
                  </p>
                </div>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* ── SECTION 6: WHAT MAKES CORE DIFFERENT ── */}
      <section className="relative w-full bg-navy-light py-24 px-6" data-id="element-1514" data-mp="1oepsegqypnbd">
        <SectionDivider data-id="element-1515" data-mp="2jo4jzpdxybud" />
        <div className="max-w-5xl mx-auto" data-id="element-1516" data-mp="2jowe0vrw41ij">
          <motion.div className="text-center mb-16" initial="hidden" whileInView="visible" viewport={{
          once: true,
          margin: '-60px'
        }} variants={stagger} data-id="element-1517" data-mp="2nnde4vcw8bqg">
            
            <motion.span variants={fadeUp} className="text-violet-electric text-xs font-semibold tracking-[0.2em] uppercase block mb-4" data-id="element-1518" data-mp="2xmkyw3lyj8nl">
              
              What Makes Core Different
            </motion.span>
            <motion.h2 variants={fadeUp} className="text-3xl sm:text-4xl font-bold text-white tracking-tight" data-id="element-1519" data-mp="36loid8p1dq2m">
              
              The capabilities that replace
              <br data-id="element-1520" data-mp="3gdc4cu2tqewp" />
              your development team.
            </motion.h2>
          </motion.div>

          <motion.div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5" initial="hidden" whileInView="visible" viewport={{
          once: true,
          margin: '-40px'
        }} variants={stagger} data-id="element-1521" data-mp="3lcoqtdps9jyr">
            
            {[{
            icon: EyeIcon,
            title: 'Full Configuration Transparency',
            desc: 'Every workflow, rule, automation, and connection is visible. Open any process and see exactly how it works, what triggers it, and what documents informed its design. The system is an open book.'
          }, {
            icon: BrainIcon,
            title: 'AI-Assisted Self-Configuration',
            desc: 'Describe changes in plain operational language. The AI suggests configuration modifications based on your existing setup and discovery data. Review, adjust, deploy — no programming required.'
          }, {
            icon: SearchIcon,
            title: 'Change Impact Visibility',
            desc: 'Before any modification goes live, see exactly what it affects downstream. "Changing this receiving workflow impacts these 3 processes and these 2 reports." No surprises.'
          }, {
            icon: FileTextIcon,
            title: 'Self-Documenting System',
            desc: 'Every configuration traces to its source document or decision. When someone asks "why does this work this way?" the system shows them. Knowledge stays in the system, not in people\'s heads.'
          }, {
            icon: UsersIcon,
            title: 'On-Demand Expert Review',
            desc: 'Flag any modification for Thruline consultant review before deploying. Build it yourself, get an expert eye when the stakes are high. Like a pull request — optional, not required.'
          }, {
            icon: PaletteIcon,
            title: 'Your Brand, Your System',
            desc: 'Core runs under your company\'s identity. Your team logs into your system, not a vendor\'s platform. When your warehouse team sees "YourCompany Operations," they own it.'
          }, {
            icon: LayersIcon,
            title: 'Full Module Suite',
            desc: 'Production scheduling, inventory management, purchasing, receiving, shipping, quality management, field operations, maintenance tracking. Start with what you need most.'
          }].map((item, i) => {
            const IconComp = item.icon;
            return <motion.div key={i} variants={fadeUp} className="rounded-2xl border border-white/[0.06] bg-white/[0.02] p-7" data-id="element-1522" data-mp="2vlx3qgkr7o2r" data-mpi={i}>
                  
                  <div className="w-9 h-9 rounded-lg bg-violet-electric/12 flex items-center justify-center mb-4" data-id="element-1523" data-mp="35lo2wvk9er96">
                    <IconComp className="w-4.5 h-4.5 text-violet-electric" data-id="element-1524" data-mp="3jc15eiedtdr4" />
                  </div>
                  <h3 className="text-sm font-semibold text-white mb-2" data-id="element-1525" data-mp="3edg6qnes324s">
                    {item.title}
                  </h3>
                  <p className="text-white/45 text-sm leading-relaxed" data-id="element-1526" data-mp="3t9flggwe80iw">
                    {item.desc}
                  </p>
                </motion.div>;
          })}
          </motion.div>
        </div>
      </section>

      {/* ── SECTION 7: COMMON QUESTIONS ── */}
      <section className="relative w-full bg-navy py-24 px-6" data-id="element-1527" data-mp="1tzpmnrkwvulz">
        <SectionDivider data-id="element-1528" data-mp="185i8vwhhatg0" />
        <div className="max-w-4xl mx-auto" data-id="element-1529" data-mp="18479s9yza9v8">
          <motion.div className="text-center mb-16" initial="hidden" whileInView="visible" viewport={{
          once: true,
          margin: '-60px'
        }} variants={stagger} data-id="element-1530" data-mp="183ea1syfskml">
            
            <motion.span variants={fadeUp} className="text-violet-electric text-xs font-semibold tracking-[0.2em] uppercase block mb-4" data-id="element-1531" data-mp="1hx4tgjfpi4vf">
              
              Common Questions
            </motion.span>
            <motion.h2 variants={fadeUp} className="text-3xl sm:text-4xl font-bold text-white tracking-tight" data-id="element-1532" data-mp="p8piq93ly8pw">
              
              Why Core
            </motion.h2>
          </motion.div>

          <motion.div className="grid grid-cols-1 md:grid-cols-2 gap-5" initial="hidden" whileInView="visible" viewport={{
          once: true,
          margin: '-40px'
        }} variants={stagger} data-id="element-1533" data-mp="z6wvefqv64m3">
            
            {[{
            q: 'Why not just buy off-the-shelf ERP?',
            a: "Because off-the-shelf systems make you work their way. You end up with workarounds, shadow spreadsheets, and processes that exist because the software can't handle how you actually operate. Core is custom-developed around your operation from day one, and you can adjust it when your operation changes — the same day, without a vendor."
          }, {
            q: 'Why not hire a team to build something custom?',
            a: "Because that requires developers, implementation consultants, and ongoing technical staff that most manufacturers can't justify. Core gives you the result — a system built around your business — without the overhead. Thruline is the development team you don't have to hire."
          }, {
            q: 'Why Core instead of a managed service like Thruline Enterprise?',
            a: "Core is for companies that want to own and evolve their system — not just use it. You get full transparency into every configuration, AI tools to make changes yourself, and the freedom to adjust things on your timeline. Enterprise is great if you want Thruline to handle everything. Core is for when you want the wrench in your own hand."
          }, {
            q: 'What if I break something?',
            a: 'Core shows you the impact of every change before you deploy it. You can see what a modification affects downstream. You can flag any change for Thruline review before it goes live. And every Core customer gets a complete sandbox environment — test any change in full isolation before it touches your live system.'
          }].map((item, i) => <motion.div key={i} variants={fadeUp} className="rounded-2xl border border-white/[0.06] bg-white/[0.02] p-8" data-id="element-1534" data-mp="12z8z5cmg6cpe" data-mpi={i}>
              
                <h3 className="text-base font-semibold text-white mb-3 leading-snug" data-id="element-1535" data-mp="1bycjejxcoy5h">
                  "{item.q}"
                </h3>
                <p className="text-white/50 text-sm leading-relaxed" data-id="element-1536" data-mp="2sbkv554xhsch">
                  {item.a}
                </p>
              </motion.div>)}
          </motion.div>
        </div>
      </section>

      {/* ── SECTION 8: COMPARISON TABLE ── */}
      <section className="relative w-full bg-navy-light py-24 px-6" data-id="element-1537" data-mp="2vwmoy6upivrx">
        <SectionDivider data-id="element-1538" data-mp="2zwwr18fdp21i" />
        <div className="max-w-4xl mx-auto" data-id="element-1539" data-mp="2zu0sbhvpi9c0">
          <motion.div className="text-center mb-14" initial="hidden" whileInView="visible" viewport={{
          once: true,
          margin: '-60px'
        }} variants={stagger} data-id="element-1540" data-mp="2lwvuu6a26isd">
            
            <motion.span variants={fadeUp} className="text-violet-electric text-xs font-semibold tracking-[0.2em] uppercase block mb-4" data-id="element-1541" data-mp="3ekhw37vtdbc2">
              
              Comparison
            </motion.span>
            <motion.h2 variants={fadeUp} className="text-3xl sm:text-4xl font-bold text-white tracking-tight" data-id="element-1542" data-mp="34vzyezitzkcn">
              
              Thruline Core vs. Traditional ERP
            </motion.h2>
          </motion.div>

          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true,
          margin: '-40px'
        }} transition={{
          duration: 0.5
        }} className="rounded-2xl border border-white/[0.06] overflow-hidden" data-id="element-1543" data-mp="1dpvdiddihmh">
            
            <table className="w-full text-sm" data-id="element-1544" data-mp="630o6cbi7kdr">
              <thead data-id="element-1545" data-mp="61z8tvnggbq8">
                <tr className="border-b border-white/[0.06]" data-id="element-1546" data-mp="617gqut2gw4p">
                  <th className="text-left px-6 py-4 text-white/30 font-medium text-xs uppercase tracking-wider w-1/3" data-id="element-1547" data-mp="226z3fmbrzja"></th>
                  <th className="text-left px-6 py-4 text-white/40 font-medium text-xs uppercase tracking-wider w-1/3" data-id="element-1548" data-mp="3t9b64aigzlrp">
                    Traditional ERP
                  </th>
                  <th className="text-left px-6 py-4 text-violet-electric font-semibold text-xs uppercase tracking-wider w-1/3 bg-violet-electric/[0.04]" data-id="element-1549" data-mp="3jiyoaq21i6p9">
                    Thruline Core
                  </th>
                </tr>
              </thead>
              <tbody data-id="element-1550" data-mp="3aj1s83dbm5k6">
                {[["How it's built", 'Vendor configures to their templates', 'Custom-developed around your actual processes'], ['Who understands it', 'The vendor (or consultants)', 'Your team — full transparency into every rule'], ['When you need a change', 'File a ticket. Wait. Pay.', 'Describe it. See the impact. Deploy it.'], ['Documentation', 'Binder from implementation day', 'Self-documenting. Always current.'], ['Your brand', "Vendor's interface with your data", 'Your system, your identity'], ['Ongoing cost model', 'License + change orders + consulting', 'Platform license + advisory as needed'], ['Time to first change', 'Weeks to months', 'Same day'], ['Knowledge retention', 'Walks out the door with employees', 'Built into the system permanently'], ['Who it requires', 'IT dept or vendor dependency', 'Your operations team + Thruline advisory']].map(([label, traditional, core], i) => <tr key={i} className={`border-b border-white/[0.04] ${i % 2 === 0 ? 'bg-white/[0.01]' : ''}`} data-id="element-1551" data-mp="ilmigkrbmg2r" data-mpi={i}>
                  
                    <td className="px-6 py-4 text-white/50 font-medium" data-id="element-1552" data-mp="njo1x6snsher">
                      {label}
                    </td>
                    <td className="px-6 py-4 text-white/35" data-id="element-1553" data-mp="2q7wh3e9gog6y">{traditional}</td>
                    <td className="px-6 py-4 text-white/70 bg-violet-electric/[0.03]" data-id="element-1554" data-mp="2l9ctzaj4syd0">
                      {core}
                    </td>
                  </tr>)}
              </tbody>
            </table>
          </motion.div>
        </div>
      </section>

      {/* ── SECTION 9: CORE VS ENTERPRISE ── */}
      <section className="relative w-full bg-navy py-24 px-6" data-id="element-1555" data-mp="38wkvdhdfvn9r">
        <SectionDivider data-id="element-1556" data-mp="33z1fkd13f2fp" />
        <div className="max-w-4xl mx-auto" data-id="element-1557" data-mp="33xq4rt98cuf3">
          <motion.div className="text-center mb-14" initial="hidden" whileInView="visible" viewport={{
          once: true,
          margin: '-60px'
        }} variants={stagger} data-id="element-1558" data-mp="3415juw2wm3gi">
            
            <motion.span variants={fadeUp} className="text-violet-electric text-xs font-semibold tracking-[0.2em] uppercase block mb-4" data-id="element-1559" data-mp="3duuuu4675a16">
              
              Two Ways to Work With Thruline
            </motion.span>
            <motion.h2 variants={fadeUp} className="text-3xl sm:text-4xl font-bold text-white tracking-tight mb-4" data-id="element-1560" data-mp="19f9egfcxfwm9">
              
              Want to own it, or want it done?
            </motion.h2>
            <motion.p variants={fadeUp} className="text-white/45 text-base max-w-xl mx-auto" data-id="element-1561" data-mp="zpg0j7pbb5eu">
              
              Both start with the same AI-powered Discovery. The difference is
              who holds the wrench after go-live.
            </motion.p>
          </motion.div>

          <motion.div className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-8" initial="hidden" whileInView="visible" viewport={{
          once: true,
          margin: '-40px'
        }} variants={stagger} data-id="element-1562" data-mp="uq3gsvtqmegh">
            
            {/* Core Card — highlighted */}
            <motion.div variants={fadeUp} className="rounded-2xl border border-violet-electric/20 bg-violet-electric/[0.04] p-8" data-id="element-1563" data-mp="lpyircafosez">
              
              <div className="flex items-center gap-3 mb-4" data-id="element-1564" data-mp="2g369arrdmhut">
                <div className="w-10 h-10 rounded-xl bg-violet-electric/20 flex items-center justify-center" data-id="element-1565" data-mp="2g2od1v4ps3mw">
                  <ZapIcon className="w-5 h-5 text-violet-electric" data-id="element-1566" data-mp="2dmopcmn267ld" />
                </div>
                <div data-id="element-1567" data-mp="29pafqtvgtxsu">
                  <h3 className="text-lg font-semibold text-white" data-id="element-1568" data-mp="24o394y5274nr">
                    Thruline Core
                  </h3>
                  <p className="text-violet-electric text-xs font-medium" data-id="element-1569" data-mp="1zptbmgvv3ryz">
                    You're here
                  </p>
                </div>
              </div>
              <p className="text-white/55 text-sm leading-relaxed mb-4" data-id="element-1570" data-mp="2sdp5ldu1r5s2">
                A custom-developed ERP you own and evolve. Thruline helps you
                plan and build it. You understand it, modify it, and grow it.
              </p>
              <ul className="space-y-2" data-id="element-1571" data-mp="2imuffydojm1c">
                {['Thruline builds the first version with you', 'You operate with full transparency and AI assistance', 'You make changes directly, on your timeline', 'Best for: companies that want to own it'].map((item, i) => <li key={i} className="flex items-start gap-2 text-white/45 text-sm" data-id="element-1572" data-mp="3inpl7zr8uera" data-mpi={i}>
                  
                    <CheckIcon className="w-3.5 h-3.5 text-violet-electric mt-0.5 flex-shrink-0" data-id="element-1573" data-mp="3nmizvvi9obt1" />
                    {item}
                  </li>)}
              </ul>
            </motion.div>

            {/* Enterprise Card */}
            <motion.div variants={fadeUp} className="rounded-2xl border border-white/[0.06] bg-white/[0.03] p-8" data-id="element-1574" data-mp="1fufkisan9f9">
              
              <div className="flex items-center gap-3 mb-4" data-id="element-1575" data-mp="5gw7eejvsj77">
                <div className="w-10 h-10 rounded-xl bg-blue-400/15 flex items-center justify-center" data-id="element-1576" data-mp="afh9ykmlveyt">
                  <BuildingIcon className="w-5 h-5 text-blue-400" data-id="element-1577" data-mp="f6plpnbercf5" />
                </div>
                <h3 className="text-lg font-semibold text-white" data-id="element-1578" data-mp="k6lqhorxj7vr">
                  Thruline Enterprise
                </h3>
              </div>
              <p className="text-white/55 text-sm leading-relaxed mb-4" data-id="element-1579" data-mp="p6zxr4lpidsq">
                A custom-configured ERP, built and maintained by Thruline. We
                handle the system. You handle the business.
              </p>
              <ul className="space-y-2 mb-6" data-id="element-1580" data-mp="1ag3vnmh0jc4d">
                {['Thruline builds the entire system', 'Thruline operates and maintains as managed service', 'Changes handled by Thruline\'s team', 'Best for: companies that want it done'].map((item, i) => <li key={i} className="flex items-start gap-2 text-white/45 text-sm" data-id="element-1581" data-mp="1jhtlinv8y301" data-mpi={i}>
                  
                    <CheckIcon className="w-3.5 h-3.5 text-blue-400 mt-0.5 flex-shrink-0" data-id="element-1582" data-mp="vrz9g8ir4igu" />
                    {item}
                  </li>)}
              </ul>
              <a href="#/solutions/enterprise" className="inline-flex items-center gap-2 text-blue-400 text-sm font-medium hover:underline" data-id="element-1583" data-mp="15gqwc28ygckw">
                
                Learn about Enterprise
                <ArrowRightIcon className="w-3.5 h-3.5" data-id="element-1584" data-mp="d0pvnn1y77jg" />
              </a>
            </motion.div>
          </motion.div>

          <motion.p initial={{
          opacity: 0
        }} whileInView={{
          opacity: 1
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.5,
          delay: 0.2
        }} className="text-white/35 text-sm text-center leading-relaxed max-w-2xl mx-auto" data-id="element-1585" data-mp="82eq3oiorj71">
            
            Some customers start with Core and bring in Thruline for
            Enterprise-style builds on major expansions. Others start with
            Enterprise and transition to Core as their team builds confidence.
            The two products complement each other.
          </motion.p>
        </div>
      </section>

      {/* ── SECTION 10: BOTTOM CTA ── */}
      <section className="relative w-full py-24 px-6 overflow-hidden" data-id="element-1586" data-mp="2tfb6oyidaukm">
        <div className="absolute inset-0" style={{
        background: 'linear-gradient(135deg, #0D0B2E 0%, #1A1452 30%, #6C5CE7 70%, #C850C0 100%)'
      }} data-id="element-1587" data-mp="2th4e4g0t1ito" />
        
        <div className="absolute inset-0 opacity-[0.04] pointer-events-none" style={{
        backgroundImage: "url(\"data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noise)'/%3E%3C/svg%3E\")"
      }} data-id="element-1588" data-mp="1hy1pkimpoemh" />
        

        <motion.div className="relative z-10 max-w-3xl mx-auto text-center" initial={{
        opacity: 0,
        y: 24
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true,
        margin: '-80px'
      }} transition={{
        duration: 0.6
      }} data-id="element-1589" data-mp="wryttukcotf2">
          
          <span className="text-white/60 text-xs font-semibold tracking-[0.2em] uppercase block mb-4" data-id="element-1590" data-mp="1fkjh8wha66bh">
            Get Started
          </span>
          <h2 className="text-3xl sm:text-5xl font-bold text-white tracking-tight mb-6" data-id="element-1591" data-mp="1ki2t5pqia709">
            Your operation is ready.
            <br data-id="element-1592" data-mp="478mm67bo5ej" />
            Let's build the system it deserves.
          </h2>
          <p className="text-white/60 text-lg mb-10 max-w-xl mx-auto" data-id="element-1593" data-mp="95s8y7ptvj7f">
            Discovery is free. We'll read your documents, map your processes,
            and show you what Core looks like for your business.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4" data-id="element-1594" data-mp="2if9hujgldra4">
            <a href="https://calendly.com/alex-thruline/learn-about-thruline" className="inline-flex items-center gap-2 px-8 py-3.5 rounded-full bg-white text-navy font-semibold text-sm hover:bg-white/90 transition-all hover:shadow-lg hover:shadow-white/10" data-id="element-1595" data-mp="2wak9zp3gbyas">
              
              Start with Discovery
              <ArrowRightIcon className="w-4 h-4" data-id="element-1596" data-mp="1zkksv698jrgl" />
            </a>
            <a href="https://calendly.com/alex-thruline/learn-about-thruline" className="inline-flex items-center px-8 py-3.5 rounded-full border border-white/30 text-white font-medium text-sm hover:border-white/60 transition-all" data-id="element-1597" data-mp="2dfdf0njms5w5">
              
              Talk to our team
            </a>
          </div>
        </motion.div>
      </section>
    </div>;
}